import { usePreferences } from 'context/preferences/PreferencesContext'

/**
 * Convert snake_case to camelCase
 * Examples:
 *   enable_campaigns -> enableCampaigns
 *   show_menu_column -> showMenuColumn
 */
const snakeToCamel = (str: string): string =>
  str.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase())

/**
 * KNOWN FLAGS (non-exhaustive; flags are read dynamically from preferences).
 * Documenting new flags here keeps them greppable since there is no static enum.
 *
 *   enable_unified_opps_endpoint -> enableUnifiedOppsEndpoint
 *     (FIR-6995) Routes opps target tables through the unified opportunities
 *     endpoint. Auto-mapped snake->camel; no alias needed.
 */

/**
 * LEGACY ALIASES - Maps snake_case backend keys to legacy frontend variable names.
 *
 * These exist because the original code used custom variable names that don't match
 * a simple snake_case -> camelCase conversion. New flags should NOT be added here.
 *
 * To phase out: Search for the legacy name, update consumers to use the auto-generated
 * camelCase name, then remove the entry from this map.
 */
const LEGACY_ALIASES: Record<string, string> = {
  // Prefix changes (show_ -> shouldShow)
  show_menu_size: 'shouldShowMenuSize',
  show_menu_column: 'shouldShowMenu',
  reputation_data: 'shouldShowReputationData',
  show_door_menu_column: 'shouldShowDoorMenu',

  // Typo preserved for backward compat
  enable_instagram_data: 'enableInstagremData',

  // Singular vs plural
  enable_instagram_percentiles: 'enableInstagramPercentile',

  // Suffix removal (_table removed)
  enable_hospitals_table: 'enableHospitals',
  enable_nursing_homes_table: 'enableNursingHomes',
  enable_lodging_table: 'enableLodging',
  enable_convenience_table: 'enableConvenience',

  // Acronym casing (SSO, HQ, AI, CnU)
  enable_cnu_table: 'enableCnUTable',
  enable_hq_state_column: 'enableHQStateColumn',
  enable_sso: 'enableSSO',
  enable_ai_sequence_generation: 'enableAISequenceGeneration',

  // Underscore in number preserved
  enable_deals_ltv_1_5: 'enableDealsLtv1_5',

  // Name changes (prefix added or word changed)
  chains_list_v2: 'enableChainsListV2',
  enable_import_wizard: 'enableImporterWizard',
  campaign_list_use_denormalized_stats: 'enableCampaignDenormalizedStats',
}

// Return type includes isLoading plus any string key for dynamic flags
type FeatureFlagReturn = Record<string, boolean> & { isLoading: boolean }

export const useFeatureFlagService = (): FeatureFlagReturn => {
  const { preferences, isLoading } = usePreferences()

  const flags: Record<string, boolean> = {}

  if (preferences?.feature_flags) {
    for (const [key, value] of Object.entries(preferences.feature_flags)) {
      const enabled = value.enabled ?? false

      // Auto-generate camelCase key
      const camelKey = snakeToCamel(key)
      flags[camelKey] = enabled

      // Also add legacy alias if one exists
      const legacyAlias = LEGACY_ALIASES[key]
      if (legacyAlias) {
        flags[legacyAlias] = enabled
      }
    }
  }

  return {
    ...flags,
    isLoading,
  } as FeatureFlagReturn
}

// Type for consumers - any flag name returns boolean
export type FeatureFlags = Record<string, boolean>
